import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Search, ChevronRight, XCircle,
  AlertTriangle, Info, CreditCard, RefreshCw,
} from 'lucide-react';
import { LicenseCard } from '../components/license/LicenseCard';
import { LicenseTabs } from '../components/license/LicenseTabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SemakLesen = ({ language }) => {
  const [searchParams] = useSearchParams();

  const [icInput, setIcInput] = useState('');
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [demoList, setDemoList] = useState([]);

  // Reset tab whenever a new result is loaded
  useEffect(() => {
    setActiveTab('overview');
  }, [result]);

  // Load the public demo list from backend on mount
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/licenses`)
      .then((res) => { if (!cancelled) setDemoList(res.data || []); })
      .catch((err) => { if (!cancelled) console.error('Gagal memuat senarai demo:', err); });
    return () => { cancelled = true; };
  }, []);

  const doSearch = useCallback(async (icVal) => {
    const clean = icVal.replace(/-/g, '').trim();
    if (!clean || clean.length < 12) return;
    setLoading(true);
    setSearched(true);
    setResult(null);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/licenses/${clean}`);
      setResult(res.data);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setNotFound(true);
      } else {
        console.error('Ralat semakan lesen:', err);
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Honour ?ic= query param on first mount
  useEffect(() => {
    const ic = searchParams.get('ic');
    if (ic) {
      setIcInput(ic);
      doSearch(ic);
    }
  }, [searchParams, doSearch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch(icInput);
  };

  const handleClear = () => {
    setIcInput('');
    setSearched(false);
    setResult(null);
    setNotFound(false);
    setLoading(false);
  };

  const tryDemoIC = (ic) => {
    setIcInput(ic);
    doSearch(ic);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f6fa' }}>
      {/* Page Header */}
      <div style={{ backgroundColor: '#003580' }} className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-xs text-white/60 mb-3">
            <Link to="/" className="hover:text-white/90 transition-colors" data-testid="breadcrumb-home">
              {language === 'bm' ? 'Laman Utama' : 'Home'}
            </Link>
            <ChevronRight size={12} />
            <span className="text-white/90">
              {language === 'bm' ? 'Semak Lesen Memandu' : 'Check Driving License'}
            </span>
          </div>
          <h1 className="text-white text-2xl font-bold">
            {language === 'bm' ? 'Semakan Lesen Memandu' : 'Driving License Check'}
          </h1>
          <p className="text-white/70 text-sm mt-1">
            {language === 'bm'
              ? 'Semak maklumat lesen memandu anda menggunakan nombor kad pengenalan'
              : 'Check your driving license information using your identity card number'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Search Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Search Form */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b" style={{ backgroundColor: '#003580' }}>
                <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Search size={16} />
                  {language === 'bm' ? 'Carian Lesen' : 'License Search'}
                </h2>
              </div>
              <div className="p-5">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="search-form">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      {language === 'bm' ? 'No. Kad Pengenalan / MyKad *' : 'IC Number / MyKad *'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={icInput}
                        onChange={(e) => {
                          setIcInput(e.target.value.replace(/[^0-9-]/g, ''));
                          if (searched) {
                            setSearched(false);
                            setResult(null);
                            setNotFound(false);
                          }
                        }}
                        maxLength={14}
                        placeholder={language === 'bm' ? 'Contoh: 860512-03-5678' : 'e.g. 860512-03-5678'}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all pr-8"
                        data-testid="ic-input"
                      />
                      {icInput.length > 0 && (
                        <button
                          type="button"
                          onClick={handleClear}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          data-testid="ic-input-clear-btn"
                        >
                          <XCircle size={16} />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {language === 'bm' ? '* Tanpa tanda sempang juga diterima' : '* Without dashes is also accepted'}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3 flex gap-2">
                    <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      {language === 'bm'
                        ? 'Maklumat lesen hanya boleh dilihat oleh pemilik lesen berdaftar. Semua maklumat adalah SULIT.'
                        : 'License information can only be viewed by the registered license owner. All information is CONFIDENTIAL.'}
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !icInput.replace(/-/g, '').trim()}
                    className="w-full py-2.5 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#003580' }}
                    onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.backgroundColor = '#002266')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#003580')}
                    data-testid="search-submit-btn"
                  >
                    {loading ? (
                      <><RefreshCw size={16} className="animate-spin" /> {language === 'bm' ? 'Sedang Mencari...' : 'Searching...'}</>
                    ) : (
                      <><Search size={16} /> {language === 'bm' ? 'Semak Lesen' : 'Check License'}</>
                    )}
                  </button>
                  {(result || notFound) && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="w-full py-2 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all border border-gray-300 text-gray-600 hover:bg-gray-50"
                      data-testid="clear-search-btn"
                    >
                      <XCircle size={15} />
                      {language === 'bm' ? 'Hapus Semakan' : 'Clear Search'}
                    </button>
                  )}
                </form>
              </div>
            </div>

            {/* Demo IC list (from backend) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="demo-list">
              <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">
                NO IC MALAYSIA
              </h3>
              <div className="space-y-2">
                {demoList.length === 0 && (
                  <div className="text-xs text-gray-400 italic">Memuat senarai...</div>
                )}
                {demoList.map((demo) => (
                  <button
                    key={demo.ic_key}
                    onClick={() => tryDemoIC(demo.ic_key)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                    data-testid={`demo-ic-${demo.ic_key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs font-semibold text-gray-700 group-hover:text-blue-700 flex items-center gap-1.5">
                          {demo.name}
                          {demo.jenisPemegang !== 'Biasa' && (
                            <span className="text-xs px-1.5 py-0.5 rounded font-bold"
                              style={{ backgroundColor: '#003580', color: 'white', fontSize: '9px' }}>
                              {demo.jenisPemegang}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">{demo.ic_key}</div>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                          demo.status === 'AKTIF' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {demo.status}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex gap-2">
                <AlertTriangle size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-yellow-800 mb-1">{language === 'bm' ? 'Peringatan' : 'Reminder'}</p>
                  <p className="text-xs text-yellow-700 leading-relaxed">
                    {language === 'bm'
                      ? 'Jika lesen memandu anda telah tamat tempoh lebih daripada 3 tahun, anda perlu menduduki ujian semula di sekolah memandu berdaftar.'
                      : 'If your driving license has been expired for more than 3 years, you are required to retake the test at a registered driving school.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {!searched && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 flex flex-col items-center justify-center text-center" data-testid="empty-state">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#f0f4ff' }}>
                  <CreditCard size={36} style={{ color: '#003580' }} />
                </div>
                <h3 className="text-gray-700 font-bold text-lg mb-2">
                  {language === 'bm' ? 'Masukkan No. Kad Pengenalan' : 'Enter Your IC Number'}
                </h3>
                <p className="text-gray-400 text-sm max-w-xs">
                  {language === 'bm'
                    ? 'Sila masukkan nombor kad pengenalan anda untuk melihat maklumat lesen memandu anda.'
                    : 'Please enter your identity card number to view your driving license information.'}
                </p>
              </div>
            )}

            {loading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 flex flex-col items-center justify-center text-center" data-testid="loading-state">
                <RefreshCw size={36} className="animate-spin mb-4" style={{ color: '#003580' }} />
                <p className="text-gray-500 text-sm">
                  {language === 'bm' ? 'Sedang mendapatkan maklumat...' : 'Fetching information...'}
                </p>
              </div>
            )}

            {notFound && !loading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 flex flex-col items-center justify-center text-center" data-testid="not-found-state">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-red-50">
                  <XCircle size={36} className="text-red-500" />
                </div>
                <h3 className="text-gray-700 font-bold text-lg mb-2">
                  {language === 'bm' ? 'Rekod Tidak Ditemui' : 'Record Not Found'}
                </h3>
                <p className="text-gray-400 text-sm max-w-xs">
                  {language === 'bm'
                    ? 'Tiada rekod lesen memandu bagi nombor kad pengenalan yang diberikan. Sila semak semula nombor anda.'
                    : 'No driving license record found for the provided IC number. Please verify your number.'}
                </p>
              </div>
            )}

            {result && !loading && (
              <div className="space-y-4" data-testid="results-panel">
                <LicenseCard result={result} language={language} />

                <LicenseTabs
                  result={result}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  language={language}
                />

                {result.status === 'TAMAT TEMPOH' && (
                  <div
                    className="rounded-xl p-5 flex flex-col md:flex-row items-start md:items-center gap-4"
                    style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}
                  >
                    <AlertTriangle size={24} className="text-yellow-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-bold text-gray-800 text-sm">
                        {language === 'bm' ? 'Lesen Anda Telah Tamat Tempoh' : 'Your License Has Expired'}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {language === 'bm'
                          ? 'Sila perbaharui lesen memandu anda secepat mungkin. Memandu dengan lesen yang tamat tempoh adalah kesalahan di bawah Akta Pengangkutan Jalan 1987.'
                          : 'Please renew your driving license as soon as possible. Driving with an expired license is an offense under the Road Transport Act 1987.'}
                      </p>
                    </div>
                    <button
                      className="flex-shrink-0 px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-all"
                      style={{ backgroundColor: '#cc0000' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#aa0000')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#cc0000')}
                      data-testid="renew-license-btn"
                    >
                      {language === 'bm' ? 'Perbaharui Lesen' : 'Renew License'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemakLesen;
